import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../hooks/useToast'
import {
  getActions, getMyOrgs, getReportData, getReportHtml, getReports, getRuns,
  requestCreateLead, requestRun, saveSettings,
} from './services/chwService'
import {
  ChwAction, ChwLeadPayload, ChwOrg, ChwReportData, ChwReportMeta, ChwRun,
  ChwSettings, DEFAULT_ICP_SPEC, EnrichSignal, IcpSpec, LeadgenSignal,
  ReportKind, settingsFromStored,
} from './types'

// The two report products are the deliverable, so each gets a first-class
// section with its own reports AND its own configuration space. Org-wide
// delivery (schedule, recipients) lives in a slim Delivery tab.

const FONT = 'Lexend Deca, sans-serif'
const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const
const DAY_NAMES: Record<string, string> = {
  MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday',
  FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday',
}
const HOURS = ['05:00', '06:00', '07:00', '08:00', '09:00']

const SIGNAL_META: Record<string, { label: string; sub: string; color: string; soft: string }> = {
  risk: { label: 'Distress', sub: 'Insolvency · strike-off · always shown first', color: '#b91c1c', soft: '#fbe9e9' },
  triggers: { label: 'Finance triggers', sub: 'New & satisfied charges · share allotments', color: '#15803d', soft: '#e6f4ec' },
  accounts: { label: 'Fresh accounts', sub: 'New financials filed', color: '#1d4ed8', soft: '#e8effb' },
  changes: { label: 'People & ownership', sub: 'Directors · PSC · name & address', color: '#b45309', soft: '#f8efdd' },
  newinc: { label: 'New incorporations', sub: 'Startups in your target sectors', color: '#0f766e', soft: '#e4f3f1' },
}
const ROW_ORDER = ['risk', 'triggers', 'changes', 'accounts']

const card: React.CSSProperties = {
  background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px',
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)', overflow: 'hidden',
}
const cardHead: React.CSSProperties = {
  padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontWeight: 600,
  fontSize: '13px', color: '#111827', fontFamily: FONT,
  display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
}
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '8px 16px', background: '#2563eb', color: 'white', fontWeight: 600,
  borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px',
  fontFamily: FONT,
}
const btnQuiet: React.CSSProperties = {
  ...btn, background: 'white', color: '#374151', border: '1px solid #d1d5db',
  fontWeight: 500,
}
const inputStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: '8px',
  border: '1px solid #d1d5db', fontSize: '13px', fontFamily: FONT,
  background: 'white', outline: 'none', boxSizing: 'border-box',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '12px', fontWeight: 600, color: '#6b7280',
  margin: '0 0 6px', fontFamily: FONT,
}
const helpStyle: React.CSSProperties = {
  fontSize: '12.5px', color: '#6b7280', fontFamily: FONT,
}
const pill = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: '999px',
  fontSize: '11px', fontWeight: 600, background: bg, color: fg, fontFamily: FONT,
  whiteSpace: 'nowrap',
})
const statusPill = (status: string) => {
  const m: Record<string, [string, string]> = {
    requested: ['#f3f4f6', '#4b5563'], running: ['#eff6ff', '#1d4ed8'],
    done: ['#ecfdf5', '#15803d'], failed: ['#fef2f2', '#dc2626'],
  }
  const [bg, fg] = m[status] || m.requested
  return pill(bg, fg)
}

const fmtDate = (iso: string | null | undefined, withYear = false) => {
  if (!iso) return ''
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''))
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', {
    weekday: withYear ? 'short' : undefined, day: 'numeric', month: 'short',
    year: withYear ? 'numeric' : undefined,
  })
}

const Switch: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> =
  ({ checked, onChange, label }) => (
    <label style={{ position: 'relative', display: 'inline-block', cursor: 'pointer', flex: 'none' }}>
      <input
        type="checkbox" checked={checked} aria-label={label}
        onChange={e => onChange(e.target.checked)}
        style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', margin: 0, cursor: 'pointer' }}
      />
      <span style={{
        display: 'block', width: 36, height: 20, borderRadius: 999,
        background: checked ? '#2563eb' : '#d1d5db', transition: 'background .15s',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: checked ? 18 : 2, width: 16, height: 16,
          borderRadius: '50%', background: 'white', boxShadow: '0 1px 2px rgba(0,0,0,.25)',
          transition: 'left .15s',
        }} />
      </span>
    </label>
  )

type Tab = 'enrichment' | 'leadgen' | 'delivery' | 'activity'

const CompaniesHouseWatchApp: React.FC = () => {
  const { user } = useAuth()
  const { showToast } = useToast()
  const [orgs, setOrgs] = useState<ChwOrg[]>([])
  const [orgId, setOrgId] = useState<string>('')
  const [tab, setTab] = useState<Tab>('enrichment')
  const [loading, setLoading] = useState(true)

  const org = useMemo(() => orgs.find(o => o.id === orgId) || null, [orgs, orgId])

  const loadOrgs = useCallback(async () => {
    try {
      const data = await getMyOrgs()
      setOrgs(data)
      setOrgId(prev => (data.find(o => o.id === prev) ? prev : data[0]?.id || ''))
    } catch (e) {
      showToast('error', 'Failed to load your Companies House Watch access')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => { loadOrgs() }, [loadOrgs])

  if (loading) {
    return <div style={{ padding: 40, fontFamily: FONT, color: '#6b7280' }}>Loading…</div>
  }
  if (!org) {
    return (
      <div style={{ padding: 40, fontFamily: FONT, maxWidth: 560 }}>
        <h2 style={{ fontSize: 18, color: '#111827', marginBottom: 8 }}>Companies House Watch</h2>
        <p style={{ color: '#6b7280', fontSize: 14 }}>
          Your account has not been assigned a reporting organisation yet.
          Ask your administrator to add you in the admin panel.
        </p>
      </div>
    )
  }

  // Three visual classes of navigation, most prominent first:
  //   products (Enrichment / Lead generation)  ->  large underlined tabs
  //   utility (Delivery / Activity)            ->  quiet text links, right
  //   within a product (Reports / Configure)   ->  the only pill toggle
  const productTab = (t: Tab, label: string): React.CSSProperties => ({
    border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: FONT,
    fontSize: 15, fontWeight: 700, padding: '10px 2px 12px',
    color: tab === t ? '#111827' : '#9ca3af',
    borderBottom: tab === t ? '3px solid #2563eb' : '3px solid transparent',
    marginBottom: -1,
  })
  const utilityLink = (t: Tab, label: string): React.CSSProperties => ({
    border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: FONT,
    fontSize: 12.5, fontWeight: 600, padding: '10px 4px 12px',
    color: tab === t ? '#374151' : '#9ca3af',
    borderBottom: tab === t ? '2px solid #9ca3af' : '2px solid transparent',
    marginBottom: -1,
  })

  return (
    <div style={{
      minHeight: '100vh', padding: '24px 28px', fontFamily: FONT,
      background: 'linear-gradient(to bottom right,#f1f5f9,#dbeafe,#e0e7ff)',
    }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
              Companies House Watch
            </h1>
            <div style={{ fontSize: 13, color: '#6b7280' }}>{org.display_name}</div>
          </div>
          {orgs.length > 1 && (
            <select
              value={orgId}
              onChange={e => setOrgId(e.target.value)}
              style={{ ...inputStyle, width: 220 }}
              aria-label="Organisation"
            >
              {orgs.map(o => <option key={o.id} value={o.id}>{o.display_name}</option>)}
            </select>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'flex-end', gap: 22, marginBottom: 18,
          borderBottom: '1px solid #d8dee9', flexWrap: 'wrap',
        }}>
          <button onClick={() => setTab('enrichment')} style={productTab('enrichment', 'Enrichment')}>
            Enrichment
          </button>
          <button onClick={() => setTab('leadgen')} style={productTab('leadgen', 'Lead generation')}>
            Lead generation
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 14 }}>
            <button onClick={() => setTab('delivery')} style={utilityLink('delivery', 'Delivery')}>
              Delivery
            </button>
            <button onClick={() => setTab('activity')} style={utilityLink('activity', 'Activity')}>
              Activity
            </button>
          </div>
        </div>

        {tab === 'enrichment' && user && (
          <ReportSection key={`e-${org.id}`} kind="digest" org={org} userId={user.id} onSaved={loadOrgs} />
        )}
        {tab === 'leadgen' && user && (
          <ReportSection key={`l-${org.id}`} kind="leadgen" org={org} userId={user.id} onSaved={loadOrgs} />
        )}
        {tab === 'delivery' && (
          <DeliveryTab key={`d-${org.id}`} org={org} onSaved={loadOrgs} />
        )}
        {tab === 'activity' && user && (
          <ActivityTab key={`a-${org.id}`} org={org} userId={user.id} />
        )}
      </div>
    </div>
  )
}

// ================================================================ sections
// One section per report product: its reports, and its own configuration.
const ReportSection: React.FC<{
  kind: ReportKind; org: ChwOrg; userId: string; onSaved: () => void
}> = ({ kind, org, userId, onSaved }) => {
  const [view, setView] = useState<'reports' | 'configure'>('reports')
  const settings = useMemo(() => settingsFromStored(org.settings), [org.settings])
  const enabled = kind === 'digest' ? settings.enrich.enabled : settings.leadgen.enabled
  const sub = kind === 'digest'
    ? 'Companies House changes on your CRM companies'
    : 'Prospects beyond your CRM, matched to your target profile'

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, color: '#6b7280' }}>
          {sub}
          {!enabled && (
            <span style={{ ...pill('#fef3c7', '#92400e'), marginLeft: 10 }}>off · not running</span>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'inline-flex', background: 'white', border: '1px solid #e5e7eb', borderRadius: 999, padding: 3, gap: 2 }}>
          {(['reports', 'configure'] as const).map(v => (
            <button
              key={v} onClick={() => setView(v)}
              style={{
                border: 'none', background: view === v ? '#e8effb' : 'transparent',
                color: view === v ? '#1d4ed8' : '#6b7280', fontSize: 12, fontWeight: 600,
                padding: '5px 14px', borderRadius: 999, cursor: 'pointer', fontFamily: FONT,
              }}
            >
              {v === 'reports' ? 'Reports' : 'Configure'}
            </button>
          ))}
        </div>
      </div>
      {view === 'reports'
        ? <ReportBrowser kind={kind} org={org} userId={userId} />
        : kind === 'digest'
          ? <EnrichConfig org={org} onSaved={onSaved} />
          : <LeadgenConfig org={org} onSaved={onSaved} />}
    </div>
  )
}

// ---------------------------------------------------------------- reports
const ReportBrowser: React.FC<{ kind: ReportKind; org: ChwOrg; userId: string }> =
  ({ kind, org, userId }) => {
  const { showToast } = useToast()
  const [reports, setReports] = useState<ChwReportMeta[]>([])
  const [selected, setSelected] = useState<ChwReportMeta | null>(null)
  const [data, setData] = useState<ChwReportData | null>(null)
  const [html, setHtml] = useState('')
  const [filter, setFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [actions, setActions] = useState<ChwAction[]>([])

  const loadActions = useCallback(() => {
    getActions(org.id).then(setActions).catch(() => {})
  }, [org.id])

  useEffect(() => {
    getReports(org.id)
      .then(all => {
        const mine = all.filter(r => r.kind === kind)
        setReports(mine)
        if (mine.length) openReport(mine[0])
      })
      .catch(() => showToast('error', 'Failed to load reports'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id, kind])

  useEffect(() => {
    if (kind !== 'leadgen') return
    loadActions()
    const t = setInterval(loadActions, 15000)
    return () => clearInterval(t)
  }, [loadActions, kind])

  const openReport = async (r: ChwReportMeta) => {
    setSelected(r)
    setData(null)
    setHtml('')
    setFilter('all')
    try {
      const d = await getReportData(r.id)
      if (d && d.sections) {
        setData(d)
      } else {
        setHtml(await getReportHtml(r.id)) // pre-v2 report: stored HTML
      }
    } catch {
      showToast('error', 'Failed to load the report')
    }
  }

  const coverage = useMemo(() =>
    reports.slice().sort((a, b) => a.report_date.localeCompare(b.report_date)).slice(-4),
    [reports])

  const actionFor = (num: string) =>
    actions.find(a => a.type === 'create_lead' && a.payload?.number === num)

  const createLead = async (payload: ChwLeadPayload) => {
    try {
      await requestCreateLead(org.id, userId, payload)
      showToast('success', 'Queued: the worker creates the lead within a minute or two')
      loadActions()
    } catch {
      showToast('error', 'Failed to queue the lead')
    }
  }

  const leadControl = (payload: ChwLeadPayload, inCrm: boolean | undefined) => {
    if (kind !== 'leadgen') return null
    if (inCrm) return <span style={pill('#e0e7ff', '#3730a3')}>IN CRM</span>
    const a = actionFor(payload.number)
    if (a?.status === 'done') {
      return (
        <span style={pill('#ecfdf5', '#15803d')}>
          {a.result === 'already_exists' ? 'In HubSpot' : 'Lead created ✓'}
        </span>
      )
    }
    if (a?.status === 'requested') {
      return <span style={pill('#f3f4f6', '#4b5563')}>Queued…</span>
    }
    return (
      <button
        onClick={() => createLead(payload)}
        title={a?.status === 'failed' ? (a.error || 'Failed, try again') : undefined}
        style={{
          ...btnQuiet, padding: '4px 10px', fontSize: 11.5,
          color: a?.status === 'failed' ? '#dc2626' : '#374151',
        }}
      >
        {a?.status === 'failed' ? 'Retry create lead' : '+ Create as lead'}
      </button>
    )
  }

  const sections = data?.sections || {}
  const counts: Record<string, number> = {}
  ROW_ORDER.forEach(k => { counts[k] = Object.keys(sections[k] || {}).length })
  const newinc = data?.new_incorporations || []
  const total = ROW_ORDER.reduce((n, k) => n + counts[k], 0)
  const chipKeys = kind === 'leadgen'
    ? ['triggers', 'accounts', 'changes', 'risk', 'newinc']
    : ['triggers', 'accounts', 'changes', 'risk']

  const chip = (key: string, label: string, n: number) => {
    const active = filter === key
    const meta = SIGNAL_META[key]
    return (
      <button
        key={key}
        onClick={() => setFilter(key)}
        style={{
          border: `1px solid ${active ? '#2563eb' : '#e5e7eb'}`,
          background: active ? '#eff6ff' : 'white',
          color: active ? '#1d4ed8' : '#6b7280',
          fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 999,
          cursor: 'pointer', fontFamily: FONT, display: 'inline-flex',
          gap: 6, alignItems: 'center',
        }}
      >
        {meta && (
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, display: 'inline-block' }} />
        )}
        {label} <b>{n}</b>
      </button>
    )
  }

  const chLink = (num: string) =>
    `https://find-and-update.company-information.service.gov.uk/company/${num}`

  const entryName = (num: string, entry: { company: unknown }) => {
    const c = entry.company as string | { name?: string } | undefined
    if (typeof c === 'string') return c || num
    return c?.name || num
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 18, alignItems: 'start' }}>
      <div style={card}>
        <div style={cardHead}>Reports <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>newest first</span></div>
        {loading && <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>Loading…</div>}
        {!loading && !reports.length && (
          <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>
            No reports yet. The next scheduled run files one here, or request
            one now from the Activity tab.
          </div>
        )}
        <div style={{ maxHeight: '68vh', overflowY: 'auto' }}>
          {reports.map(r => (
            <button
              key={r.id}
              onClick={() => openReport(r)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 16px', border: 'none', cursor: 'pointer',
                borderBottom: '1px solid #f3f4f6', fontFamily: FONT,
                background: selected?.id === r.id ? '#eff6ff' : 'white',
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                {fmtDate(r.report_date, true)}
              </span>
              <br />
              <span style={{ fontSize: 11, color: '#6b7280' }}>
                {r.window_since ? `covers since ${fmtDate(r.window_since)}` : ''}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ ...card, minHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        <div style={cardHead}>
          <span>{selected
            ? `${kind === 'digest' ? 'Enrichment digest' : 'Prospecting report'} · ${fmtDate(selected.report_date, true)}`
            : 'Report'}</span>
          {data?.swept != null && (
            <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>
              {data.swept.toLocaleString()} companies checked
            </span>
          )}
        </div>

        {selected && coverage.length > 1 && (
          <div style={{ padding: '14px 18px 16px', borderBottom: '1px solid #e5e7eb', background: '#fafbfd' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 8 }}>
              Coverage · continuous, nothing repeated
            </div>
            <div style={{ display: 'flex', gap: 3, marginBottom: 22 }}>
              {coverage.map(r => (
                <div key={r.id} style={{
                  flex: 1, height: 22, borderRadius: 4, position: 'relative',
                  background: r.id === selected.id ? '#2563eb' : '#e0e7ff',
                }}>
                  <span style={{ position: 'absolute', top: 26, left: 0, fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {fmtDate(r.window_since)} – {fmtDate(r.report_date)}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>
              Each report starts where the last one ended, so an item appears in
              exactly one report.
            </div>
          </div>
        )}

        {data && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '12px 18px', borderBottom: '1px solid #e5e7eb' }}>
              {chip('all', 'All', total + (kind === 'leadgen' ? newinc.length : 0))}
              {chipKeys.map(k => chip(
                k, SIGNAL_META[k].label,
                k === 'newinc' ? newinc.length : counts[k],
              ))}
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {ROW_ORDER.filter(k => filter === 'all' || filter === k).map(key => (
                Object.entries(sections[key] || {}).map(([num, entry]) => {
                  const meta = SIGNAL_META[key]
                  const filings = entry.filings || []
                  const dates = Array.from(new Set(filings.map(f => f.date).filter(Boolean))).sort()
                  const cats = Array.from(new Set(filings.map(f => f.category).filter(Boolean))).sort()
                  const co = typeof entry.company === 'object' ? entry.company : null
                  return (
                    <div key={`${key}-${num}`} style={{
                      display: 'grid', gridTemplateColumns: '4px 240px 1fr auto',
                      gap: '0 14px', borderBottom: '1px solid #f3f4f6',
                      padding: '12px 18px 12px 0', alignItems: 'start',
                    }}>
                      <div style={{ background: meta.color, borderRadius: 2, alignSelf: 'stretch' }} />
                      <div>
                        <b style={{ fontSize: 13.5, color: '#111827' }}>{entryName(num, entry)}</b>
                        {entry.owner ? (
                          <span style={{ ...pill('#e0e7ff', '#3730a3'), marginLeft: 6, fontSize: 10 }}>
                            {entry.owner}
                          </span>
                        ) : null}
                        <br />
                        <span style={{ fontSize: 11.5, color: '#6b7280' }}>
                          {num} · {dates[dates.length - 1] ? fmtDate(dates[dates.length - 1]) : ''} · {cats.join('; ')}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: '#1f2937' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.03em', color: meta.color }}>
                          {meta.label.toUpperCase()}
                        </span>{': '}
                        {entry.note}
                        {entry.lender ? ` Lender on latest charge: ${entry.lender}.` : ''}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', paddingTop: 2 }}>
                        <a
                          href={chLink(num)} target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color: '#2563eb', whiteSpace: 'nowrap' }}
                        >
                          Companies House ↗
                        </a>
                        {co && leadControl({
                          number: num, name: co.name || num, locality: co.locality,
                          region: co.region, postcode: co.postcode, created: co.created,
                          signal: key, note: entry.note, lender: entry.lender,
                        }, co.in_crm)}
                      </div>
                    </div>
                  )
                })
              ))}

              {(filter === 'all' || filter === 'newinc') && kind === 'leadgen' && newinc.map(c => (
                <div key={`newinc-${c.number}`} style={{
                  display: 'grid', gridTemplateColumns: '4px 240px 1fr auto',
                  gap: '0 14px', borderBottom: '1px solid #f3f4f6',
                  padding: '12px 18px 12px 0', alignItems: 'start',
                }}>
                  <div style={{ background: SIGNAL_META.newinc.color, borderRadius: 2, alignSelf: 'stretch' }} />
                  <div>
                    <b style={{ fontSize: 13.5, color: '#111827' }}>{c.name}</b><br />
                    <span style={{ fontSize: 11.5, color: '#6b7280' }}>
                      {c.number} · {[c.locality, c.region || c.postcode].filter(Boolean).join(', ')}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#1f2937' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.03em', color: SIGNAL_META.newinc.color }}>
                      NEW INCORPORATION
                    </span>{': '}
                    incorporated {fmtDate(c.created)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', paddingTop: 2 }}>
                    <a
                      href={chLink(c.number)} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: '#2563eb', whiteSpace: 'nowrap' }}
                    >
                      Companies House ↗
                    </a>
                    {leadControl({
                      number: c.number, name: c.name, locality: c.locality,
                      region: c.region, postcode: c.postcode, created: c.created,
                    }, c.in_crm)}
                  </div>
                </div>
              ))}

              {total === 0 && newinc.length === 0 && (
                <div style={{ padding: 20, color: '#6b7280', fontSize: 13 }}>
                  Nothing reportable in this window. That is the report: no news.
                </div>
              )}
            </div>

            <div style={{ padding: '12px 18px', fontSize: 12, color: '#6b7280', borderTop: '1px solid #e5e7eb', background: '#fafbfd' }}>
              This page is always the full report. Routine filings (no-update
              confirmation statements, resolutions) are excluded automatically.
            </div>
          </>
        )}

        {!data && html && (
          <iframe title="report" srcDoc={html} sandbox="" style={{ border: 'none', flex: 1, width: '100%', minHeight: '65vh' }} />
        )}
        {!data && !html && (
          <div style={{ padding: 20, color: '#6b7280', fontSize: 13 }}>
            {selected ? 'Loading report…' : 'Select a report on the left.'}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- config
const sigRow = (
  meta: { label: string; sub: string; color: string },
  checked: boolean, disabled: boolean, onChange: (v: boolean) => void,
  badge?: React.ReactNode,
) => (
  <label
    key={meta.label}
    style={{
      display: 'flex', gap: 10, alignItems: 'baseline', padding: '8px 8px',
      borderRadius: 7, cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.45 : 1, fontFamily: FONT,
    }}
  >
    <input
      type="checkbox" checked={checked} disabled={disabled}
      onChange={e => onChange(e.target.checked)}
      style={{ accentColor: '#2563eb' }}
    />
    <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, display: 'inline-block', position: 'relative', top: -1 }} />
    <span>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{meta.label} {badge}</span><br />
      <span style={{ fontSize: 11.5, color: '#6b7280' }}>{meta.sub}</span>
    </span>
  </label>
)

const SaveRow: React.FC<{ saving: boolean; onSave: () => void }> = ({ saving, onSave }) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
    <button onClick={onSave} disabled={saving} style={{ ...btn, opacity: saving ? 0.6 : 1 }}>
      {saving ? 'Saving…' : 'Save changes'}
    </button>
    <span style={{ fontSize: 12, color: '#6b7280' }}>
      Applies from the next report. Nothing already reported is ever re-sent.
    </span>
  </div>
)

function useSectionSave(org: ChwOrg, onSaved: () => void) {
  const { showToast } = useToast()
  const [saving, setSaving] = useState(false)
  const save = async (s: ChwSettings) => {
    setSaving(true)
    try {
      await saveSettings(org.id, s)
      showToast('success', 'Saved. Changes apply from the next run')
      onSaved()
    } catch {
      showToast('error', 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }
  return { saving, save }
}

const EnrichConfig: React.FC<{ org: ChwOrg; onSaved: () => void }> = ({ org, onSaved }) => {
  const [s, setS] = useState<ChwSettings>(() => settingsFromStored(org.settings))
  const { saving, save } = useSectionSave(org, onSaved)
  const list = s.enrich.signals as string[]
  const toggle = (key: EnrichSignal, on: boolean) => setS({
    ...s,
    enrich: { ...s.enrich, signals: on ? [...list, key] as EnrichSignal[] : list.filter(x => x !== key) as EnrichSignal[] },
  })

  return (
    <div style={{ ...card, maxWidth: 720 }}>
      <div style={cardHead}>
        <span>Enrichment configuration</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: '#6b7280' }}>
          {s.enrich.enabled ? 'Running' : 'Off'}
          <Switch checked={s.enrich.enabled} label="Enrichment on or off"
            onChange={v => setS({ ...s, enrich: { ...s.enrich, enabled: v } })} />
        </span>
      </div>
      <div style={{ padding: '14px 18px' }}>
        <label style={labelStyle}>What to watch on your CRM companies</label>
        <div style={{ opacity: s.enrich.enabled ? 1 : 0.5 }}>
          {(['triggers', 'accounts', 'changes', 'risk'] as EnrichSignal[]).map(k =>
            sigRow(SIGNAL_META[k], list.includes(k), !s.enrich.enabled, on => toggle(k, on),
              k === 'risk'
                ? <span style={{ ...pill('#e6f4ec', '#15803d'), marginLeft: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>Recommended</span>
                : undefined))}
        </div>
        <label style={{ ...labelStyle, marginTop: 14 }}>Personal owner emails</label>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: '#374151', cursor: 'pointer' }}>
          <input
            type="checkbox" checked={s.email.owners}
            onChange={e => setS({ ...s, email: { ...s.email, owners: e.target.checked } })}
            style={{ marginTop: 3, accentColor: '#2563eb' }}
          />
          <span>
            Send each HubSpot record owner their own companies
            <br />
            <span style={helpStyle}>
              Owners get a personal email covering only companies they own.
              The recipients on the Delivery tab always get everything.
            </span>
          </span>
        </label>
        <SaveRow saving={saving} onSave={() => save(s)} />
      </div>
    </div>
  )
}

const LeadgenConfig: React.FC<{ org: ChwOrg; onSaved: () => void }> = ({ org, onSaved }) => {
  const [s, setS] = useState<ChwSettings>(() => settingsFromStored(org.settings))
  const [spec, setSpec] = useState<IcpSpec>(() =>
    settingsFromStored(org.settings).icp_spec || JSON.parse(JSON.stringify(DEFAULT_ICP_SPEC)))
  const { saving, save } = useSectionSave(org, onSaved)
  const list = s.leadgen.signals as string[]
  const toggle = (key: LeadgenSignal, on: boolean) => setS({
    ...s,
    leadgen: { ...s.leadgen, signals: on ? [...list, key] as LeadgenSignal[] : list.filter(x => x !== key) as LeadgenSignal[] },
  })

  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 860 }}>
      <div style={card}>
        <div style={cardHead}>
          <span>Lead generation configuration</span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: '#6b7280' }}>
            {s.leadgen.enabled ? 'Running' : 'Off'}
            <Switch checked={s.leadgen.enabled} label="Lead generation on or off"
              onChange={v => setS({ ...s, leadgen: { ...s.leadgen, enabled: v } })} />
          </span>
        </div>
        <div style={{ padding: '14px 18px', opacity: s.leadgen.enabled ? 1 : 0.5 }}>
          <label style={labelStyle}>What to report on prospects</label>
          {(['newinc', 'triggers', 'accounts', 'changes', 'risk'] as LeadgenSignal[]).map(k =>
            sigRow(SIGNAL_META[k], list.includes(k), !s.leadgen.enabled, on => toggle(k, on)))}
        </div>
      </div>

      <div style={card}>
        <div style={cardHead}>
          <span>{org.display_name} target profile</span>
          <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>
            yours alone: changes here never affect any other organisation
          </span>
        </div>
        <div style={{ padding: '14px 18px', opacity: s.leadgen.enabled ? 1 : 0.5 }}>
          <IcpSummaryChips spec={spec} />
          <IcpEditor spec={spec} onChange={setSpec} />
        </div>
      </div>

      <SaveRow saving={saving} onSave={() => save({ ...s, icp_spec: spec })} />
    </div>
  )
}

// ---------------------------------------------------------------- ICP
const IcpSummaryChips: React.FC<{ spec: IcpSpec }> = ({ spec }) => {
  const sicTotal = Object.values(spec.groups).reduce((n, g) => n + Object.keys(g.sics).length, 0)
  const chipStyle: React.CSSProperties = {
    background: '#fafbfd', border: '1px solid #e5e7eb', borderRadius: 999,
    padding: '4px 12px', fontSize: 12, fontWeight: 600, color: '#6b7280', fontFamily: FONT,
  }
  const postcodes = spec.geography?.postcode_prefixes || []
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
      <span style={chipStyle}><b style={{ color: '#111827' }}>{Object.keys(spec.groups).length}</b> segments</span>
      <span style={chipStyle}><b style={{ color: '#111827' }}>{sicTotal}</b> SIC codes</span>
      <span style={chipStyle}>established <b style={{ color: '#111827' }}>{spec.established_years}+ yrs</b></span>
      {spec.priority_prefixes.length > 0 && (
        <span style={chipStyle}><b style={{ color: '#111827' }}>{spec.priority_prefixes.join(', ')}</b> priority</span>
      )}
      <span style={chipStyle}>
        {postcodes.length ? <><b style={{ color: '#111827' }}>{postcodes.join(', ')}</b> areas</> : <b style={{ color: '#111827' }}>UK-wide</b>}
      </span>
    </div>
  )
}

const IcpEditor: React.FC<{ spec: IcpSpec; onChange: (s: IcpSpec) => void }> = ({ spec, onChange }) => {
  // Segments edited as "code label" lines, same format the worker validates.
  const segKeys = Object.keys(spec.groups)
  const sicsToText = (sics: Record<string, string>) =>
    Object.entries(sics).map(([c, l]) => (l && l !== c ? `${c} ${l}` : c)).join('\n')
  const textToSics = (text: string) => {
    const out: Record<string, string> = {}
    text.split('\n').forEach(line => {
      const m = line.trim().match(/^(\d{4,5})\s*(.*)$/)
      if (m) out[m[1]] = m[2] || m[1]
    })
    return out
  }
  const patchGroup = (key: string, patch: Partial<IcpSpec['groups'][string]>) =>
    onChange({ ...spec, groups: { ...spec.groups, [key]: { ...spec.groups[key], ...patch } } })

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label style={labelStyle}>Established at least (years)</label>
          <input
            type="number" min={0} max={20} value={spec.established_years}
            onChange={e => onChange({ ...spec, established_years: +e.target.value || 0 })}
            style={{ ...inputStyle, width: 90 }}
          />
        </div>
        <div>
          <label style={labelStyle}>Priority registrations</label>
          <input
            type="text" value={spec.priority_prefixes.join(', ')}
            onChange={e => onChange({
              ...spec,
              priority_prefixes: e.target.value.split(',').map(x => x.trim().toUpperCase()).filter(Boolean),
            })}
            style={{ ...inputStyle, width: 150 }}
            placeholder="NI, SC"
          />
        </div>
        <div>
          <label style={labelStyle}>Postcode areas (blank = UK-wide)</label>
          <input
            type="text"
            value={(spec.geography?.postcode_prefixes || []).join(', ')}
            onChange={e => {
              const prefixes = e.target.value.split(',').map(x => x.trim().toUpperCase()).filter(Boolean)
              onChange({ ...spec, geography: prefixes.length ? { postcode_prefixes: prefixes } : undefined })
            }}
            style={{ ...inputStyle, width: 220 }}
            placeholder="BT, M, LS"
          />
        </div>
      </div>
      {segKeys.map(key => {
        const g = spec.groups[key]
        return (
          <div key={key} style={{
            border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px',
            margin: '12px 0', background: '#fafbfd',
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label style={labelStyle}>Segment name</label>
                <input
                  type="text" value={g.label} style={{ ...inputStyle, width: '100%' }}
                  onChange={e => patchGroup(key, { label: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Sweep up to</label>
                <input
                  type="number" min={0} step={50} value={g.cap}
                  style={{ ...inputStyle, width: 100 }}
                  onChange={e => patchGroup(key, { cap: +e.target.value || 0 })}
                />
              </div>
              <button
                type="button"
                style={{ ...btnQuiet, color: '#dc2626' }}
                onClick={() => {
                  const groups = { ...spec.groups }
                  delete groups[key]
                  onChange({ ...spec, groups })
                }}
              >
                Remove
              </button>
            </div>
            <label style={{ ...labelStyle, marginTop: 10 }}>
              SIC codes, one per line, label after the code
            </label>
            <textarea
              rows={Math.min(8, Object.keys(g.sics).length + 1)}
              defaultValue={sicsToText(g.sics)}
              onBlur={e => patchGroup(key, { sics: textToSics(e.target.value) })}
              style={{ ...inputStyle, width: '100%', fontFamily: 'Consolas, monospace', fontSize: 12 }}
            />
            <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4 }}>
              {Object.keys(g.sics).length} SIC codes in this segment
            </div>
          </div>
        )
      })}
      <button
        type="button" style={btnQuiet}
        onClick={() => onChange({
          ...spec,
          groups: { ...spec.groups, [`seg${Date.now()}`]: { label: 'New segment', cap: 300, sics: {} } },
        })}
      >
        + Add segment
      </button>
    </div>
  )
}

// ---------------------------------------------------------------- delivery
function nextReportText(s: ChwSettings): string {
  const { frequency, day, hour } = s.cadence
  if (frequency === 'paused') {
    return 'Reports are paused. Resuming covers the whole gap in one report.'
  }
  const now = new Date()
  const next = new Date(now)
  next.setDate(now.getDate() + 1)
  if (frequency === 'weekdays') {
    while (next.getDay() === 0 || next.getDay() === 6) next.setDate(next.getDate() + 1)
  } else {
    const want = DAYS.indexOf((day || 'MON') as typeof DAYS[number]) // 0=MON
    while ((next.getDay() + 6) % 7 !== want) next.setDate(next.getDate() + 1)
  }
  const label = next.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })
  return `Next report: ${label}, ready by ${hour}. Covers everything since the previous report.`
}

const DeliveryTab: React.FC<{ org: ChwOrg; onSaved: () => void }> = ({ org, onSaved }) => {
  const [s, setS] = useState<ChwSettings>(() => settingsFromStored(org.settings))
  const { saving, save } = useSectionSave(org, onSaved)
  const set = (patch: Partial<ChwSettings>) => setS(prev => ({ ...prev, ...patch }))

  const summary = (() => {
    const { frequency, day, hour } = s.cadence
    if (frequency === 'paused') return 'Paused. Resume any time; the first report back covers the entire gap.'
    if (frequency === 'weekdays') return `Every weekday at ${hour}. Each report covers only what's new since the last one.`
    return `Weekly on ${DAY_NAMES[day] || day} at ${hour}. Each report covers the full week since the last one.`
  })()

  const segBtn = (active: boolean): React.CSSProperties => ({
    border: 'none', background: active ? '#2563eb' : 'transparent',
    color: active ? 'white' : '#6b7280', fontSize: 13, fontWeight: 600,
    padding: '7px 14px', borderRadius: 7, cursor: 'pointer', fontFamily: FONT,
  })

  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 780 }}>
      <div style={{
        background: '#eff6ff', color: '#1d4ed8', borderRadius: 12,
        padding: '13px 18px', fontSize: 13.5, fontWeight: 600,
      }}>
        {summary}
      </div>

      <div style={card}>
        <div style={cardHead}>
          Schedule
          <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>
            applies to both report types; each is configured in its own tab
          </span>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'inline-flex', background: '#fafbfd', border: '1px solid #e5e7eb', borderRadius: 9, padding: 3, gap: 2, flexWrap: 'wrap' }}>
            {([['weekdays', 'Every weekday'], ['weekly', 'Once a week'], ['paused', 'Paused']] as const).map(([v, l]) => (
              <button key={v} style={segBtn(s.cadence.frequency === v)}
                onClick={() => set({ cadence: { ...s.cadence, frequency: v } })}>
                {l}
              </button>
            ))}
          </div>

          {s.cadence.frequency !== 'paused' && (
            <div style={{ display: 'flex', gap: 22, marginTop: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {s.cadence.frequency === 'weekly' && (
                <div>
                  <label style={labelStyle}>On</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {DAYS.map(d => (
                      <button
                        key={d}
                        onClick={() => set({ cadence: { ...s.cadence, day: d } })}
                        style={{
                          width: 44, padding: '6px 0', borderRadius: 7,
                          border: `1px solid ${s.cadence.day === d ? '#2563eb' : '#d1d5db'}`,
                          background: s.cadence.day === d ? '#2563eb' : 'white',
                          color: s.cadence.day === d ? 'white' : '#6b7280',
                          fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
                        }}
                      >
                        {d.charAt(0) + d.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label style={labelStyle}>Ready by</label>
                <select
                  value={s.cadence.hour}
                  onChange={e => set({ cadence: { ...s.cadence, hour: e.target.value } })}
                  style={{ ...inputStyle, width: 100 }}
                >
                  {HOURS.map(h => <option key={h}>{h}</option>)}
                </select>
              </div>
            </div>
          )}

          <div style={{ marginTop: 14, fontSize: 12.5, color: '#6b7280' }}>
            {nextReportText(s)}
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={cardHead}>
          Email
          <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>
            the portal always keeps the full report
          </span>
        </div>
        <div style={{ padding: '16px 18px' }}>
          {([
            ['highlights', 'Highlights email', 'Counts, distress alerts, and the top items per signal. A button opens the full report here.'],
            ['full', 'Full report email', 'The entire report in the email. Best for small watchlists; switches itself to highlights past 40 items.'],
            ['none', 'No email', 'Portal only. Reports still run and file here.'],
          ] as const).map(([v, t, dsc]) => (
            <label key={v} style={{
              display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px',
              border: `1px solid ${s.email.style === v ? '#2563eb' : '#e5e7eb'}`,
              background: s.email.style === v ? '#fafbfd' : 'white',
              borderRadius: 9, cursor: 'pointer', marginBottom: 8,
            }}>
              <input
                type="radio" name="mailstyle" checked={s.email.style === v}
                onChange={() => set({ email: { ...s.email, style: v } })}
                style={{ marginTop: 3, accentColor: '#2563eb' }}
              />
              <span>
                <span style={{ fontWeight: 600, fontSize: 13.5, color: '#111827' }}>
                  {t}
                  {v === 'highlights' && (
                    <span style={{ ...pill('#e6f4ec', '#15803d'), marginLeft: 8, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                      Recommended
                    </span>
                  )}
                </span><br />
                <span style={helpStyle}>{dsc}</span>
              </span>
            </label>
          ))}

          {s.email.style !== 'none' && (
            <div style={{ marginTop: 14 }}>
              <label style={labelStyle}>Send to (comma separated)</label>
              <input
                type="text" value={s.email.admin_emails.join(', ')}
                onChange={e => set({
                  email: {
                    ...s.email,
                    admin_emails: e.target.value.split(',').map(x => x.trim()).filter(Boolean),
                  },
                })}
                style={{ ...inputStyle, width: '100%' }}
                placeholder="ops@yourcompany.com"
              />
              {/* bcc lives in settings but is admin-managed; not exposed here */}
            </div>
          )}
        </div>
      </div>

      <SaveRow saving={saving} onSave={() => save(s)} />
    </div>
  )
}

// ---------------------------------------------------------------- activity
const runOutcome = (r: ChwRun): string => {
  const s = r.summary as Record<string, Record<string, unknown>> | null
  if (!s) return ''
  const bits: string[] = []
  const digest = s.digest as Record<string, unknown> | undefined
  const leadgen = s.leadgen as Record<string, unknown> | undefined
  if (digest?.swept != null) {
    const counts = (digest.counts || {}) as Record<string, number>
    const inReport = Object.values(counts).reduce((a, b) => a + b, 0)
    bits.push(`${digest.swept} companies checked · ${inReport} in report`)
  }
  if (leadgen?.swept != null) {
    bits.push(`${leadgen.swept} prospects swept · ${leadgen.with_activity ?? 0} with activity`)
  }
  const email = s.email as Record<string, unknown> | undefined
  if (email && email.style !== 'none') {
    const sent = Number(email.emails_sent || 0) + Number(email.emails_queued || 0)
    if (sent) bits.push(`emailed to ${sent} recipient${sent !== 1 ? 's' : ''}`)
  }
  return bits.join(' · ')
}

const ActivityTab: React.FC<{ org: ChwOrg; userId: string }> = ({ org, userId }) => {
  const { showToast } = useToast()
  const [runs, setRuns] = useState<ChwRun[]>([])
  const [requesting, setRequesting] = useState(false)
  const settings = useMemo(() => settingsFromStored(org.settings), [org.settings])

  const load = useCallback(() => {
    getRuns(org.id).then(setRuns).catch(() => {})
  }, [org.id])

  useEffect(() => {
    load()
    const t = setInterval(load, 15000)
    return () => clearInterval(t)
  }, [load])

  const request = async (kind: ReportKind) => {
    setRequesting(true)
    try {
      await requestRun(org.id, userId, kind)
      showToast('success', 'Run requested. The worker picks it up within a few minutes')
      load()
    } catch {
      showToast('error', 'Failed to request the run')
    } finally {
      setRequesting(false)
    }
  }

  const fmtTs = (iso: string | null) =>
    iso ? iso.replace('T', ' ').slice(0, 16) : ''

  return (
    <div style={{ ...card, maxWidth: 860 }}>
      <div style={{ ...cardHead, alignItems: 'center' }}>
        <span style={{ marginRight: 'auto' }}>Activity</span>
        {settings.enrich.enabled && (
          <button style={btnQuiet} disabled={requesting} onClick={() => request('digest')}>
            Run enrichment now
          </button>
        )}
        {settings.leadgen.enabled && (
          <button style={btn} disabled={requesting} onClick={() => request('leadgen')}>
            Run lead generation now
          </button>
        )}
      </div>
      {!runs.length && (
        <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>No runs recorded yet.</div>
      )}
      {runs.map(r => (
        <div key={r.id} style={{
          display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap',
          padding: '11px 16px', borderBottom: '1px solid #f3f4f6', fontSize: 13,
        }}>
          <span style={statusPill(r.status)}>{r.status}</span>
          <span style={{ fontWeight: 600, color: '#111827' }}>
            {r.kind === 'nightly' ? 'Scheduled reports'
              : r.kind === 'digest' ? 'Enrichment' : 'Lead generation'}
          </span>
          <span style={{ color: '#6b7280', fontSize: 12 }}>
            {fmtTs(r.started_at || r.created_at)}
            {r.finished_at ? ` → ${fmtTs(r.finished_at).slice(-5)}` : ''}
          </span>
          <span style={{ color: '#6b7280', fontSize: 12 }}>{runOutcome(r)}</span>
          {r.error && <span style={{ color: '#dc2626', fontSize: 12 }}>{r.error}</span>}
        </div>
      ))}
      <div style={{ padding: '10px 16px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
        A failed night never loses coverage. The next report simply covers the
        whole gap since the last successful one.
      </div>
    </div>
  )
}

export default CompaniesHouseWatchApp
